import { FormFieldValidation } from '@moia-dev/pace-web'
import {
  Controller,
  type ControllerProps,
  type FieldValues,
} from 'react-hook-form'
import {
  MultiSelectComboField,
  type MultiSelectComboFieldProps,
} from '../Basic'

export type FormMultiSelectComboFieldProps<
  FormFieldValues extends FieldValues,
  Value,
> = Omit<
  MultiSelectComboFieldProps<Value>,
  'selectedOptions' | 'setSelectedOption' | 'id'
> &
  Pick<
    ControllerProps<FormFieldValues>,
    'control' | 'name' | 'rules' | 'disabled'
  > & {
    optionToId?: (option: Value) => string
    tagSize?: 'default' | 'large'
    onChange?: (value: Value[]) => void
  }

export const FormMultiSelectComboField = <
  FormFieldValues extends FieldValues,
  Value,
>({
  label,
  name,
  control,
  rules,
  caption,
  disabled,
  tagSize,
  ...props
}: FormMultiSelectComboFieldProps<FormFieldValues, Value>) => {
  const maxLength =
    typeof rules?.maxLength === 'number' ? rules.maxLength : undefined
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { value, onChange }, fieldState }) => (
        <MultiSelectComboField
          required={Boolean(rules?.required)}
          maxLength={maxLength}
          caption={caption}
          validation={
            fieldState.error?.message && (
              <FormFieldValidation type="error">
                {fieldState.error.message}
              </FormFieldValidation>
            )
          }
          label={label}
          disabled={disabled}
          {...props}
          selectedOptions={value || []}
          setSelectedOption={values => {
            onChange(values)
            props.onChange?.(values)
          }}
          error={Boolean(fieldState.error?.type)}
          tagSize={tagSize}
        />
      )}
    />
  )
}
